import React, { useState, useEffect } from 'react'
import { apiService } from '@templatean/shared/services'
import { outlookService } from '../services/outlookService'

interface EmailMetrics {
  id: string
  subject: string
  recipient: string
  sentAt: string
  status: 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced'
  opens: number
  firstOpenedAt?: string
  lastOpenedAt?: string
  clicks: number
  clickedLinks: Array<{
    url: string
    clicks: number
    lastClickedAt: string
  }>
  deviceInfo?: {
    os: string
    browser: string
    device: string
  }
}

interface TrackerStats {
  totalSent: number
  totalOpened: number
  totalClicked: number
  avgOpenRate: number
  avgClickRate: number
}

const EmailTracker: React.FC = () => {
  const [metrics, setMetrics] = useState<EmailMetrics[]>([])
  const [stats, setStats] = useState<TrackerStats>({
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0,
    avgOpenRate: 0,
    avgClickRate: 0
  })
  const [loading, setLoading] = useState(true)
  const [selectedEmail, setSelectedEmail] = useState<EmailMetrics | null>(null)
  const [timeFilter, setTimeFilter] = useState<'24h' | '7d' | '30d' | 'all'>('7d')

  useEffect(() => {
    loadMetrics()
  }, [timeFilter])

  const loadMetrics = async () => {
    try {
      const response = await apiService.get<{ metrics: EmailMetrics[]; stats: TrackerStats }>(`/email-tracker/metrics?period=${timeFilter}`)
      setMetrics(response.data.metrics)
      setStats(response.data.stats)
    } catch (error) {
      console.error('Error loading email metrics:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTrackNewEmail = async () => {
    try {
      const itemId = await outlookService.getItemId()
      if (!itemId) {
        alert('Please select an email to track')
        return
      }

      await apiService.post('/email-tracker/track', { itemId })
      alert('Email tracking enabled!')
      loadMetrics()
    } catch (error) {
      console.error('Error enabling tracking:', error)
      alert('Failed to enable tracking')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  const getStatusIcon = (status: EmailMetrics['status']) => {
    switch (status) {
      case 'sent': return '📤'
      case 'delivered': return '✉️'
      case 'opened': return '👁️'
      case 'clicked': return '🖱️'
      case 'bounced': return '⚠️'
      default: return '📧'
    }
  }

  const getStatusColor = (status: EmailMetrics['status']) => {
    switch (status) {
      case 'sent': return '#gray'
      case 'delivered': return '#blue'
      case 'opened': return '#green'
      case 'clicked': return '#purple'
      case 'bounced': return '#red'
      default: return '#gray'
    }
  }

  if (loading) {
    return <div className="loading">Loading email metrics...</div>
  }

  return (
    <div className="email-tracker">
      <div className="tracker-header">
        <h2>Email Tracker</h2>
        <button onClick={handleTrackNewEmail} className="btn btn-primary">
          Track Current Email
        </button>
      </div>

      <div className="tracker-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.totalSent}</div>
          <div className="stat-label">Emails Sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalOpened}</div>
          <div className="stat-label">Emails Opened</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.avgOpenRate.toFixed(1)}%</div>
          <div className="stat-label">Open Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalClicked}</div>
          <div className="stat-label">Links Clicked</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.avgClickRate.toFixed(1)}%</div>
          <div className="stat-label">Click Rate</div>
        </div>
      </div>

      <div className="tracker-controls">
        <div className="time-filter">
          <button
            className={`filter-btn ${timeFilter === '24h' ? 'active' : ''}`}
            onClick={() => setTimeFilter('24h')}
          >
            Last 24 Hours
          </button>
          <button
            className={`filter-btn ${timeFilter === '7d' ? 'active' : ''}`}
            onClick={() => setTimeFilter('7d')}
          >
            Last 7 Days
          </button>
          <button
            className={`filter-btn ${timeFilter === '30d' ? 'active' : ''}`}
            onClick={() => setTimeFilter('30d')}
          >
            Last 30 Days
          </button>
          <button
            className={`filter-btn ${timeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setTimeFilter('all')}
          >
            All Time
          </button>
        </div>
      </div>

      <div className="metrics-list">
        <table className="metrics-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Subject</th>
              <th>Recipient</th>
              <th>Sent</th>
              <th>Opens</th>
              <th>Clicks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map(metric => (
              <tr key={metric.id} className="metric-row">
                <td>
                  <span 
                    className="status-icon"
                    style={{ color: getStatusColor(metric.status) }}
                  >
                    {getStatusIcon(metric.status)}
                  </span>
                </td>
                <td className="subject-cell">{metric.subject}</td>
                <td>{metric.recipient}</td>
                <td>{formatDate(metric.sentAt)}</td>
                <td>
                  {metric.opens > 0 ? (
                    <span className="metric-value opens">{metric.opens}</span>
                  ) : (
                    <span className="metric-value">-</span>
                  )}
                </td>
                <td>
                  {metric.clicks > 0 ? (
                    <span className="metric-value clicks">{metric.clicks}</span>
                  ) : (
                    <span className="metric-value">-</span>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => setSelectedEmail(metric)}
                    className="btn btn-sm"
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedEmail && (
        <div className="email-details-modal" onClick={() => setSelectedEmail(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Email Details</h3>
              <button 
                onClick={() => setSelectedEmail(null)}
                className="close-btn"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h4>Basic Information</h4>
                <p><strong>Subject:</strong> {selectedEmail.subject}</p>
                <p><strong>Recipient:</strong> {selectedEmail.recipient}</p>
                <p><strong>Sent:</strong> {formatDate(selectedEmail.sentAt)}</p>
                <p><strong>Status:</strong> {selectedEmail.status}</p>
              </div>

              {selectedEmail.opens > 0 && (
                <div className="detail-section">
                  <h4>Open Activity</h4>
                  <p><strong>Total Opens:</strong> {selectedEmail.opens}</p>
                  {selectedEmail.firstOpenedAt && (
                    <p><strong>First Opened:</strong> {formatDate(selectedEmail.firstOpenedAt)}</p>
                  )}
                  {selectedEmail.lastOpenedAt && (
                    <p><strong>Last Opened:</strong> {formatDate(selectedEmail.lastOpenedAt)}</p>
                  )}
                </div>
              )}

              {selectedEmail.clickedLinks.length > 0 && (
                <div className="detail-section">
                  <h4>Link Clicks</h4>
                  <table className="clicks-table">
                    <thead>
                      <tr>
                        <th>URL</th>
                        <th>Clicks</th>
                        <th>Last Clicked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEmail.clickedLinks.map((link, index) => (
                        <tr key={index}>
                          <td className="url-cell">{link.url}</td>
                          <td>{link.clicks}</td>
                          <td>{formatDate(link.lastClickedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedEmail.deviceInfo && (
                <div className="detail-section">
                  <h4>Device Information</h4>
                  <p><strong>OS:</strong> {selectedEmail.deviceInfo.os}</p>
                  <p><strong>Browser:</strong> {selectedEmail.deviceInfo.browser}</p>
                  <p><strong>Device:</strong> {selectedEmail.deviceInfo.device}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmailTracker